import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QUERY_DEBOUNCE_TIME } from '../../../core/constants/constants';
import { matchPattern } from '../../../core/utils/string';
import {
  TRACKER_ADD_NEW_TARGET_CLICK,
  TRACKER_SELECT_EXISTING_TARGET,
  TRACKER_TARGET_LIST_EMPTY_RESULT
} from '../constants';
import { Target } from '../models/target';
import { isTargetStatusFailed, TargetsFactory } from '../utils';
import { TileGrid } from '@hevoio/hevo-ui-toolkit';
import { DEFAULT_TILE_HEIGHT } from '../../../layout/constants/tileGrid';
import EntityTile from '../../../layout/components/entity-tile/EntityTile';
import { EntityTileProps } from '../../../layout/interfaces/tile';
import RetryApiAlert from '../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import SearchArea from '../../../list/components/search-area/SearchArea';
import StickySearchWrapper from '../../../list/components/sticky-search-wrapper/StickySearchWrapper';
import { TileSkeletonGrid } from '@hevoio/hevo-ui-toolkit';
import { HdButton, HdIcon, HdPane } from '../../../components';
import { WizardStepBaseProps } from '../../../wizard/interfaces/wizardStep';
import { useWizardStepData } from '../../../wizard/hooks/useWizardStepData';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import ActivateTargetAPI from '../ActivateTargetAPI';
import styles from './styles.module.scss';
import TargetTilesFactory from './tiles-factory';
import { HdTimeFormat } from '../../../date-time/components/calendar-format/HdCalendar';
import { ActivationStatusService } from '../../../core/services/activation-status.service';
import useService from '../../../injector/hooks/useService';

export interface SelectTargetInnerProps {
  defaultSearch?: string;
  saveSearchValue?: Function;
  onTargetSelect: Function;
  onCreateNewClick: Function;
  skipStep: Function;
}

export function SelectTarget({
  initialData,
  completeStep,
  onDataChange,
  skip,
  markDirty
}: WizardStepBaseProps) {
  const [ data, setData ] = useWizardStepData(initialData, onDataChange);

  const saveSearchValue = (searchValue) => {
    setData({
      ...data,
      searchValue
    });
  };

  const onTargetSelect = (target: Target) => {
    if (!data.selectedTarget || data.selectedTarget.id !== target.id) {
      markDirty();
    }

    setData({
      ...data,
      selectedTarget: target
    });

    completeStep();
  };

  const createNewClick = () => {
    if (data.selectedTarget) {
      markDirty();
    }

    setData({
      ...data,
      selectedTarget: undefined,
      searchValue: null
    });

    completeStep();
  };

  return (
    <SelectTargetInner
      defaultSearch={data.searchValue}
      saveSearchValue={saveSearchValue}
      onTargetSelect={onTargetSelect}
      onCreateNewClick={createNewClick}
      skipStep={skip}
    />
  );
}

export default function SelectTargetInner({
  defaultSearch,
  saveSearchValue,
  onTargetSelect,
  onCreateNewClick,
  skipStep
}: SelectTargetInnerProps) {
  const [targetTiles, setTargetTiles] = useState<Array<EntityTileProps>>([]);
  const [filteredTargetTiles, setFilteredTargetTiles] = useState<Array<EntityTileProps>>([]);
  const searchRef = useRef<string>(defaultSearch);

  const [loading, setLoading] = useState<boolean>(true);
  const [loadingError, setLoadingError] = useState(null);

  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);

  const { eventTrack } = useAnalyticsTracker();

  useEffect(() => {
    getTargets();
  }, []);

  const getTargets = async () => {
    setLoading(true);
    setLoadingError(null);

    try {
      const { data } = await ActivateTargetAPI.getTargets();
      let targetList = TargetsFactory([...data]);

      targetList = targetList.sort((targetA, targetB) => {
        if (isTargetStatusFailed(targetA) && isTargetStatusFailed(targetB)) {
          return 0;
        }

        return isTargetStatusFailed(targetA) ? 1 : -1;
      });

      const _targetTiles = TargetTilesFactory(targetList);
      setTargetTiles(_targetTiles);
      _filterTargets(_targetTiles, searchRef.current);

      if (!targetList || !targetList.length) {
        skipStep();
      }
    } catch (e) {
      setLoadingError(e);
    } finally {
      setLoading(false);
    }
  };

  const showTilesGrid = !loading && !loadingError && filteredTargetTiles.length > 0;
  const showNoItemBox = !loading && !loadingError && filteredTargetTiles.length === 0;
  const showRetryAPIBox = !loading && loadingError;
  const _activateStatusService = useService(ActivationStatusService);
  const showActivateBanner = _activateStatusService.shouldShowActivationBanner();

  const onSearch = useCallback(
    searchInput => {
      searchRef.current = searchInput || '';
      saveSearchValue(searchInput);

      _filterTargets(targetTiles, searchInput);
    },
    [targetTiles]
  );

  const _filterTargets = (targets: Array<EntityTileProps>, searchTerm: string) => {
    let filteredTargets = targets;
    if (searchTerm) {
      filteredTargets = targets.filter(targetTile =>
        matchPattern([targetTile.data.name, targetTile.data.targetTypeDisplay], searchTerm)
      );
    }

    setFilteredTargetTiles(filteredTargets);

    if (!filteredTargets && searchTerm) {
      eventTrack({
        action: TRACKER_TARGET_LIST_EMPTY_RESULT,
        properties: {
          searchTerm
        }
      });
    }
  };

  const onTargetTileClick = (target: Target) => {
    if (isTargetStatusFailed(target)) {
      return;
    }

    eventTrack({
      action: TRACKER_SELECT_EXISTING_TARGET,
      properties: {
        targetType: target.targetType
      }
    });

    if (onTargetSelect) {
      onTargetSelect(target);
    }
  };

  const onAddTargetClick = () => {
    eventTrack({
      action: TRACKER_ADD_NEW_TARGET_CLICK
    });

    if (onCreateNewClick) {
      onCreateNewClick();
    }
  };

  const dataIdGenerator = getDataIdGenerator('targetList');

  const activateShutDownDate = _activateStatusService.getActivationSunsetEpoch();

  return (
    <div className={styles.selectTargetContainer}>
      <div className='scrollable-content bg-surface' id='scrollable-view' ref={setScrollElement}>
        <div className='body-container'>
          <div className='header-container mb-5'>
            <div className='header'>Select a Target</div>

            <div className='subheader'>Select an existing Target or create a new one</div>
          </div>

          <StickySearchWrapper>
            <SearchArea
              dataId={dataIdGenerator('')}
              autofocus
              onSearch={onSearch}
              debounceInterval={QUERY_DEBOUNCE_TIME}
              placeholder='Search Targets'
              collapsible={false}
              defaultSearch={defaultSearch}
            />
          </StickySearchWrapper>

          <div className='d-flex justify-center mt-5'>
            {showActivateBanner && (
              <HdPane icon='deprecate' variant='warning-faded' className='w-75 mb-0 px-4'>
                <span className='text-bold'>Important update</span>: Effective{' '}
                <HdTimeFormat value={activateShutDownDate} />, Activate will be available only for
                Salesforce and HubSpot Targets. For more information, please contact Hevo Support or
                your account executive.
              </HdPane>
            )}
          </div>

          <div className='node-container'>
            {loading ? <TileSkeletonGrid tileCount={4} /> : null}

            {showTilesGrid ? (
              <TileGrid
                tileHeight={DEFAULT_TILE_HEIGHT}
                tiles={filteredTargetTiles}
                onTileClick={onTargetTileClick}
                TileComponent={EntityTile}
                scrollElement={scrollElement}
              />
            ) : null}

            {showNoItemBox ? (
              <div className='no-item-box'>
                <div className='no-item-box-icon-container'>
                  <HdIcon name='search' className='no-item-box-icon' />
                </div>

                <span className='no-item-box-title'>No Target Found 😞</span>

                <span className='no-item-box-desc'>
                  No matching Targets found for the above search criteria
                </span>
              </div>
            ) : null}

            {showRetryAPIBox ? (
              <RetryApiAlert error={loadingError} dataId={dataIdGenerator('')} />
            ) : null}
          </div>
        </div>
      </div>

      {!loading && targetTiles?.length ? (
        <div className='fixed-footer flex-shrink-0'>
          <div className='center-flex-row text-secondary'>Click here to add a new Target</div>

          <HdButton
            icon='plus'
            onClick={onAddTargetClick}
            className='ml-5'
            dataId={dataIdGenerator('add-new-target')}
          >
            Add Target
          </HdButton>
        </div>
      ) : null}
    </div>
  );
}
